import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { PUBLIC } from "@/constants/testIds";
import { useSiteTheme } from "@/components/public/useSiteTheme";
import { Cookie } from "lucide-react";
import SeoHead from "@/components/public/SeoHead";
import AdSlot from "@/components/public/AdSlot";
import TurnstileWidget from "@/components/public/TurnstileWidget";
import { renderFeed } from "@/components/public/FeedLayouts";

function CookieBanner({ siteSlug }) {
  const key = `civi.cookie.${siteSlug}`;
  const [shown, setShown] = useState(() => !localStorage.getItem(key));
  if (!shown) return null;
  function decide(accept) {
    localStorage.setItem(key, accept ? "accepted" : "rejected");
    setShown(false);
  }
  return (
    <div
      className="fixed bottom-4 left-4 md:left-1/2 md:-translate-x-1/2 md:right-auto right-4 md:bottom-6 md:w-[520px] border p-4 shadow-2xl"
      style={{ background: "var(--site-surface)", borderColor: "var(--site-rule)", color: "var(--site-text)", zIndex: 100000 }}
    >
      <div className="flex items-start gap-3">
        <Cookie size={18} />
        <div className="flex-1 text-sm leading-relaxed">
          We use cookies to operate this site, measure traffic, and personalize content. Read our{" "}
          <Link to={`/site/${siteSlug}/privacy`} className="underline">Privacy Policy</Link> &amp;{" "}
          <Link to={`/site/${siteSlug}/ccpa`} className="underline">CCPA Notice</Link>.
        </div>
      </div>
      <div className="mt-3 flex gap-2 justify-end">
        <button data-testid={PUBLIC.cookieReject} onClick={() => decide(false)} className="text-xs px-3 py-1.5 border" style={{ borderColor: "var(--site-rule)" }}>Reject</button>
        <button data-testid={PUBLIC.cookieAccept} onClick={() => decide(true)} className="text-xs px-3 py-1.5" style={{ background: "var(--site-accent)", color: "#fff" }}>Accept</button>
      </div>
    </div>
  );
}

function NewsletterBlock({ siteSlug }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [msg, setMsg] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");

  async function submit(channel, value) {
    if (!consent) { setMsg("Please confirm legal consent."); return; }
    try {
      await publicApi.post("/public/forms/newsletter", {
        site_slug: siteSlug, channel,
        email: channel === "email" ? value : undefined,
        phone: channel === "sms" ? value : undefined,
        consent_legal: true,
        turnstile_token: turnstileToken || undefined,
      });
      setMsg(`Subscribed via ${channel}. Confirmation pending.`);
      if (channel === "email") setEmail(""); else setPhone("");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed.");
    }
  }

  return (
    <section className="border-y py-8 my-10" style={{ borderColor: "var(--site-rule)" }}>
      <div className="max-w-3xl mx-auto px-4">
        <h3 className="display text-2xl font-bold">Stay informed.</h3>
        <p className="text-sm mt-1 opacity-70">One concise briefing, weekly. No spam.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <form onSubmit={(e) => { e.preventDefault(); submit("email", email); }}>
            <div className="text-[11px] uppercase tracking-widest opacity-60 mb-1.5">Email</div>
            <div className="flex">
              <input
                data-testid={PUBLIC.newsletterEmailInput}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 bg-transparent border px-3 py-2 text-sm"
                style={{ borderColor: "var(--site-rule)", color: "var(--site-text)" }}
              />
              <button data-testid={PUBLIC.newsletterEmailSubmit} className="px-4 text-sm font-semibold" style={{ background: "var(--site-accent)", color: "#fff" }}>
                Subscribe
              </button>
            </div>
          </form>
          <form onSubmit={(e) => { e.preventDefault(); submit("sms", phone); }}>
            <div className="text-[11px] uppercase tracking-widest opacity-60 mb-1.5">SMS Breaking Alerts</div>
            <div className="flex">
              <input
                data-testid={PUBLIC.newsletterSmsInput}
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 555 0123"
                className="flex-1 bg-transparent border px-3 py-2 text-sm"
                style={{ borderColor: "var(--site-rule)", color: "var(--site-text)" }}
              />
              <button data-testid={PUBLIC.newsletterSmsSubmit} className="px-4 text-sm font-semibold" style={{ background: "var(--site-accent)", color: "#fff" }}>
                Subscribe
              </button>
            </div>
          </form>
        </div>

        <label className="mt-4 flex items-start gap-2 text-xs opacity-80 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
          <span>
            I consent to receive periodic communications. I can unsubscribe at any time via the link in each message or the <Link to={`/site/${siteSlug}/unsubscribe`} className="underline">Unsubscribe page</Link>.
            Standard message/data rates may apply for SMS.
          </span>
        </label>
        <TurnstileWidget onToken={setTurnstileToken} />
        {msg && <div className="mt-3 text-xs opacity-80">{msg}</div>}
      </div>
    </section>
  );
}

function Footer({ site }) {
  return (
    <footer className="mt-10 border-t" style={{ borderColor: "var(--site-rule)" }}>
      <div className="max-w-6xl mx-auto px-4 py-8 text-sm grid grid-cols-2 md:grid-cols-4 gap-6 opacity-80">
        <div>
          <div className="display text-lg font-bold">{site.name}</div>
          {site.tagline && <div className="text-xs opacity-70 mt-1">{site.tagline}</div>}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest opacity-60 mb-2">Sections</div>
          <ul className="space-y-1">
            {site.categories.map((c) => (
              <li key={c.slug}><Link to={`/site/${site.slug}/category/${c.slug}`}>{c.name}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest opacity-60 mb-2">Company</div>
          <ul className="space-y-1">
            <li><Link to={`/site/${site.slug}/about`}>About</Link></li>
            <li><Link to={`/site/${site.slug}/contact`}>Contact</Link></li>
            <li><Link to={`/site/${site.slug}/contact?kind=advertise`}>Advertise</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest opacity-60 mb-2">Legal</div>
          <ul className="space-y-1">
            <li><Link to={`/site/${site.slug}/privacy`}>Privacy</Link></li>
            <li><Link to={`/site/${site.slug}/terms`}>Terms</Link></li>
            <li><Link to={`/site/${site.slug}/ccpa`}>CCPA Notice</Link></li>
            <li><Link to={`/site/${site.slug}/unsubscribe`}>Unsubscribe</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t py-4 text-center text-[11px] opacity-60" style={{ borderColor: "var(--site-rule)" }}>
        © {new Date().getFullYear()} {site.name}. All rights reserved.
      </div>
    </footer>
  );
}

function Nav({ site, current }) {
  return (
    <nav className="border-b" style={{ borderColor: "var(--site-rule)" }}>
      <div className="max-w-6xl mx-auto px-4 flex flex-wrap items-center gap-6 py-3 text-sm">
        <Link to={`/site/${site.slug}`} className="display text-lg font-bold">{site.name}</Link>
        <div className="flex flex-wrap gap-4">
          {site.categories.map((c) => (
            <Link
              key={c.slug}
              data-testid={PUBLIC.category(c.slug)}
              to={`/site/${site.slug}/category/${c.slug}`}
              className={`text-sm transition-opacity ${current === c.slug ? "opacity-100" : "opacity-70 hover:opacity-100"}`}
            >
              {c.name}
            </Link>
          ))}
        </div>
        <div className="ml-auto text-[11px] uppercase tracking-widest opacity-60">
          {site.template}/{site.variant}
        </div>
      </div>
    </nav>
  );
}

export default function PublicSiteHome() {
  const { slug, category } = useParams();
  const [site, setSite] = useState(null);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const q = category ? `?category=${category}` : "";
        const r = await publicApi.get(`/public/sites/${slug}/feed${q}`);
        setSite(r.data.site);
        setItems(r.data.items);
      } catch (e) {
        setErr(e.response?.data?.detail || e.message);
      }
    })();
  }, [slug, category]);

  useSiteTheme(site);

  if (err) return <div className="public-site min-h-screen flex items-center justify-center p-6 text-center">Site not found. <Link to="/admin" className="underline ml-2">Back to admin</Link></div>;
  if (!site) return <div className="public-site min-h-screen flex items-center justify-center"><div className="opacity-60 mono">Loading…</div></div>;

  const AdHeader = <AdSlot site={site} placement="header" />;
  const AdInline = <AdSlot site={site} placement="in_article" />;
  const AdSidebar = <AdSlot site={site} placement="sidebar" />;

  return (
    <div className="public-site">
      <SeoHead
        site={site}
        title={category ? site.categories.find((c) => c.slug === category)?.name : undefined}
        description={site.tagline || site.description}
      />
      <Nav site={site} current={category} />
      <header data-testid={PUBLIC.hero} className="border-b" style={{ borderColor: "var(--site-rule)" }}>
        <div className="max-w-6xl mx-auto px-4 py-6">
          <h1 className="display text-3xl md:text-5xl font-black tracking-tight">{site.name}</h1>
          {site.tagline && <div className="text-sm opacity-70 mt-2">{site.tagline}</div>}
        </div>
      </header>

      {items.length === 0 ? (
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="display text-2xl font-bold">No articles published yet.</div>
          <div className="opacity-70 mt-3 text-sm">Admin: ingest from sources, rewrite, and publish to see content here.</div>
        </div>
      ) : (
        renderFeed({ site, items, AdHeader, AdInline, AdSidebar })
      )}

      <NewsletterBlock siteSlug={site.slug} />
      <AdSlot site={site} placement="footer" />
      <Footer site={site} />
      <CookieBanner siteSlug={site.slug} />
    </div>
  );
}
